import { Check, Move, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Slider } from '../../ui/slider';

interface ImageCropperModalProps {
  isOpen: boolean;
  imageFile: File | null;
  /** Dialog heading — the cropper is shared by user profiles and contacts. */
  title?: string;
  onClose: () => void;
  onCropComplete: (croppedBlob: Blob) => void;
}

const VIEWPORT_SIZE = 280; // 280x280 px square UI viewport
const OUTPUT_SIZE = 512; // 512x512 px crisp output avatar

export function ImageCropperModal({
  isOpen,
  imageFile,
  title = 'Crop Profile Picture',
  onClose,
  onCropComplete,
}: ImageCropperModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageObj, setImageObj] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const positionStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (!imageFile) {
      setImageSrc(null);
      setImageObj(null);
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageSrc(url);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImageObj(img);
      setZoom(1);
      setRotation(0);
      setPosition({ x: 0, y: 0 });
    };
    img.src = url;

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageFile]);

  // Compute scale based on fitting image into VIEWPORT_SIZE
  const getScale = useCallback(() => {
    if (!imageObj) return 1;
    const isRotated = rotation % 180 !== 0;
    const width = isRotated ? imageObj.height : imageObj.width;
    const height = isRotated ? imageObj.width : imageObj.height;
    // Cover viewport
    const baseScale = Math.max(VIEWPORT_SIZE / width, VIEWPORT_SIZE / height);
    return baseScale * zoom;
  }, [imageObj, rotation, zoom]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    positionStartRef.current = { ...position };
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setPosition({
        x: positionStartRef.current.x + dx,
        y: positionStartRef.current.y + dy,
      });
    },
    [isDragging]
  );

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setZoom((prev) => Math.min(Math.max(prev + delta, 1), 3));
  };

  const handleRotate = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleCrop = () => {
    if (!imageObj) return;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Clear background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    // Save context state
    ctx.save();

    // Move to center of canvas
    ctx.translate(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2);

    // Scale from UI viewport size to output size (512x512)
    const uiToOutputRatio = OUTPUT_SIZE / VIEWPORT_SIZE;
    ctx.translate(position.x * uiToOutputRatio, position.y * uiToOutputRatio);

    // Rotate
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw scaled image centered
    const scale = getScale();
    const drawWidth = imageObj.width * scale * uiToOutputRatio;
    const drawHeight = imageObj.height * scale * uiToOutputRatio;

    ctx.drawImage(imageObj, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

    ctx.restore();

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCropComplete(blob);
          onClose();
        }
      },
      'image/png',
      0.95
    );
  };

  const scale = getScale();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Drag and zoom to position your photo in the 1:1 square box.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-6 py-4">
          {/* Viewport Box */}
          <div
            className="relative overflow-hidden rounded-xl border-2 border-primary/20 bg-black/90 cursor-grab active:cursor-grabbing select-none shadow-inner flex items-center justify-center"
            style={{ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            {imageObj && (
              <div
                className="absolute pointer-events-none transition-transform duration-75 ease-out"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                <img
                  src={imageSrc || undefined}
                  alt="Crop preview"
                  draggable={false}
                  style={{
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }}
                />
              </div>
            )}

            {/* Circular / Square Overlay Mask */}
            <div className="absolute inset-0 border-[24px] border-black/40 rounded-xl pointer-events-none" />
            <div className="absolute inset-0 border border-white/40 rounded-full pointer-events-none" />

            {/* Move Hint */}
            <div className="absolute bottom-2 right-2 bg-black/60 text-white/80 p-1 rounded-md text-xs pointer-events-none flex items-center gap-1">
              <Move className="h-3 w-3" />
              <span>Drag</span>
            </div>
          </div>

          {/* Controls */}
          <div className="w-full space-y-4 px-2">
            <div className="flex items-center gap-3">
              <ZoomOut className="h-4 w-4 text-muted-foreground shrink-0" />
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.05}
                onValueChange={(val) => setZoom(val[0])}
                className="grow"
              />
              <ZoomIn className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>

            <div className="flex justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRotate}
                className="gap-1.5 text-xs"
              >
                <RotateCw className="h-3.5 w-3.5" />
                Rotate 90°
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleCrop} className="gap-1.5">
            <Check className="h-4 w-4" />
            Apply & Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
